import * as React from 'react';
import { useParams } from 'react-router-dom';
import Grid from '@mui/system/Unstable_Grid';
import { Stack, Box, Typography, Button, Container, useTheme } from '@mui/material';
import { Megaphone as MegaphoneIcon } from '@phosphor-icons/react/dist/ssr';
import { paths } from '@/paths';
import { useAnnouncement } from './hooks/useAnnouncement';
import { AnnouncementInfoCard } from './components/announcement-info-card';
import AnnouncementCard from '@/pages/announcements/components/AnnouncementCard';
import { CommunityPageHeader } from '@/components/common/community-page-header';
import { useUser } from '@/hooks/v2/use-user';
import { Seo } from '@/components/seo';

export default function AnnouncementPage(): React.JSX.Element {
  const { announcementId } = useParams();
  const { announcement, loading, error } = useAnnouncement(announcementId!);
  const { community } = useUser();
  const theme = useTheme();

  // Show announcement not found error
  if (!loading && error === 'Announcement not found') {
    return (
      <>
        <Seo title="Announcement Not Found" />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            py: 3,
            backgroundColor: theme.palette.background.default,
            minHeight: '100vh',
          }}
        >
          <Container maxWidth="xl">
            <CommunityPageHeader
              icon={<MegaphoneIcon size={36} />}
              subtitle="The announcement you're looking for doesn't exist or has been removed"
              title="Announcement Not Found"
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: '200px',
                  height: '200px',
                  color: 'primary.main',
                  opacity: 0.8,
                  mb: 4,
                }}
              >
                <MegaphoneIcon size={200} />
              </Box>

              <Typography color="text.secondary" variant="body1" sx={{ mb: 2 }}>
                Announcement ID: <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: '4px' }}>{announcementId}</code>
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => window.history.back()}
                  sx={{ minWidth: 120 }}
                >
                  Go Back
                </Button>
                <Button
                  variant="contained"
                  href={paths.announcements.index}
                  sx={{ minWidth: 120 }}
                >
                  View All Announcements
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title="Announcement" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="xl">
          <CommunityPageHeader
            breadcrumbs={[
              {
                label: 'Home',
                href: '/',
              },
              {
                label: 'Announcements',
                href: paths.announcements.index,
              },
              {
                label: announcement?.subject || 'Announcement',
              },
            ]}
            icon={<MegaphoneIcon size={36} />}
            subtitle="View announcement details and information"
            title={announcement?.subject || 'Loading Announcement...'}
          />
          <Stack spacing={4}>
            <Grid container spacing={4}>
              <Grid md={7} xs={12}>
                <Stack spacing={4}>
                  {announcement ? <AnnouncementCard data={announcement} excludeLink /> : null}
                </Stack>
              </Grid>
              <Grid md={5} xs={12}>
                <Stack spacing={4}>
                  {announcement ? <AnnouncementInfoCard announcement={announcement} error={error} isLoading={loading} /> : null}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Container>
      </Box>
    </>
  );
}
